import { useState, useEffect, useRef } from 'react';
import * as Cases from '../slices/casesSlice';
import * as UI from '../slices/uiSlice';
import { useAppDispatch, useAppSelector } from '../slices/index';
import '../App.css';
import CaseListItem from './caselistitem';
import SearchCases from './searchcases';
import { AnyAction } from '@reduxjs/toolkit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import hub_logo from '../assets/Main_Hub_Icon_Large.svg';
import clear_filter from '../assets/Clear_Filter.svg';

const CaseList = () => {
	const [casesData, setCasesData] = useState<Cases.caseListState[]>([]);
	const cases = useAppSelector(state => state.cases);
	const user = useAppSelector(state => state.user);
	const dispatch = useAppDispatch();
	const [filterByStatusOptions, setFilterByStatusOptions] = useState([
		{ value: "0", label: "No Status" },
		{ value: "1", label: "Pre-Onboarding" },
		{ value: "2", label: "No Client Paperwork" },
		{ value: "3", label: "Onboarded with Searches" },
		{ value: "4", label: "On-Boarding Complete" },
		{ value: "5", label: "Reporting/Title Checking/Contracts Complete" },
		{ value: "6", label: "Pre-Exchange/Financing/Dealing With Enquiries Complete" },
		{ value: "7", label: "Pre-Completion/Completing Complete" },
		{ value: "8", label: "Exchanged and Completed" },
	]);
	const [selectedOptions, setSelectedOptions] = useState<string[]>(cases.filterStatus);
	const [filterByStatusOpened, setFilterByStatusOpened] = useState(false);
	const filterByStatusDropdownRef = useRef<HTMLDivElement>(null);

	const handleFilterByStatusOption = (value: string) => {
		const updatedSelectedOptions = [...selectedOptions];
		if (updatedSelectedOptions.includes(value)) {
			const index = updatedSelectedOptions.indexOf(value);
			updatedSelectedOptions.splice(index, 1);
		} else {
			updatedSelectedOptions.push(value);
		}

		setSelectedOptions(updatedSelectedOptions);
		dispatch(Cases.setFilterStatus(updatedSelectedOptions));
		dispatch(Cases.searchCases({ searchStr: cases.searchStr, isWishlisted: false }));
	};

	const handleClickOutside = (event: MouseEvent) => {
		if (filterByStatusDropdownRef.current && !filterByStatusDropdownRef.current.contains(event.target as Node)) {
			setFilterByStatusOpened(false);
		}
	};

	const actionOnClick = (item: Cases.sortType) => {
		dispatch(Cases.setSortType(item));
	}

	const handleScroll = () => {
		dispatch((UI.closeMenu()) as unknown as AnyAction);
	};

	const doTeamOnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const setft = () => new Promise((resolve) => resolve(dispatch(Cases.setFilterTeam(e.target.value))));
		setft().then(() => {
			dispatch(Cases.searchCases({ searchStr: cases.searchStr, isWishlisted: false }));
		});
	}

	const doTypeOnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const setft = () => new Promise((resolve) => resolve(dispatch(Cases.setFilterType(e.target.value))));
		setft().then(() => {
			dispatch(Cases.searchCases({ searchStr: cases.searchStr, isWishlisted: false }));
		});
	}

	const doSiteOnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const setft = () => new Promise((resolve) => resolve(dispatch(Cases.setFilterSite(e.target.value))));
		setft().then(() => {
			dispatch(Cases.searchCases({ searchStr: cases.searchStr, isWishlisted: false }));
		});
	}

	const doWishlistOnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const setfw = () => new Promise((resolve) => resolve(dispatch(Cases.setFilterWishlist(e.target.value))));
		setfw().then(() => {
			dispatch(Cases.searchCases({ searchStr: cases.searchStr, isWishlisted: false }));
		});
	}

	const clearFilters = () => {
		const setft = () => new Promise((resolve) => resolve(dispatch(Cases.setFilterTeam(''))));
		const setfte = () => new Promise((resolve) => resolve(dispatch(Cases.setFilterType(''))));
		const setfst = () => new Promise((resolve) => resolve(dispatch(Cases.setFilterSite(''))));
		const setfs = () => new Promise((resolve) => resolve(dispatch(Cases.setFilterStage(''))));
		const setfstatus = () => new Promise((resolve) => resolve(dispatch(Cases.setFilterStatus(['1', '2', '3', '4', '6', '7', '8']))));
		const setfw = () => new Promise((resolve) => resolve(dispatch(Cases.setFilterWishlist(''))));
		setft().then(() => setfte().then(() => setfst().then(() => setfs().then(() => setfstatus().then(() => setfw().then(() => {
			dispatch(Cases.searchCases({ searchStr: cases.searchStr, isWishlisted: false }));
		}))))));
	}
	const siteInList = (site: Cases.siteListState, list: Cases.siteListState[]): boolean => {
		for (const s of list) {
			if (s.site === site.site && s.siteid === site.siteid) {
				return true;
			}
		}
		return false;
	}
	const getCaseSites = (): Cases.siteListState[] => {
		let sl: Cases.siteListState[] = [];
		for (const c of cases.cases) {
			let s = { siteid: (c.siteid ? c.siteid : null), site: (c.site ? c.site : "Unassigned") };
			if (!siteInList(s, sl)) {
				sl.push(s);
			}
		}
		sl.sort(compareSite);

		return sl;
	}
	const handlePageChange = (isIncrement: boolean) => {
		if (isIncrement && cases.skip + 1 >= Math.ceil(casesData.length / cases.rowsPerPage)) {
			return;
		} else if (!isIncrement && cases.skip - 1 < 0) {
			return;
		} else {
			if (!cases.isLoading) {
				dispatch(Cases.setSkip((isIncrement ? cases.skip + 1 : cases.skip - 1).toString()));
			}
		}
	}
	const handleRowsPerPage = (e: React.ChangeEvent<HTMLSelectElement>) => {
		if (!cases.isLoading) {
			dispatch(Cases.setSkip('0'));
			dispatch(Cases.setRowsPerPage(e.target.value));
		}
	}
	function compareSite(a: Cases.siteListState, b: Cases.siteListState) {
		if (!a.siteid) { return -1; }
		if (!b.siteid) { return 1; }
		if (a.site.trim() < b.site.trim()) {
			return -1;
		}
		if (a.site.trim() > b.site.trim()) {
			return 1;
		}
		return 0;
	}

	useEffect(() => {
		dispatch(Cases.searchCases({ searchStr: cases.searchStr, isWishlisted: false }));
	}, [cases.searchStr]);

	useEffect(() => {
		dispatch(Cases.searchCases({ searchStr: "", isWishlisted: false }));

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	useEffect(() => {
		dispatch(Cases.setFilterStatus(selectedOptions));
	}, [selectedOptions])

	useEffect(() => {
		setSelectedOptions(cases.filterStatus);
	}, [cases.filterStatus])

	useEffect(() => {
		dispatch(Cases.setSkip('0'));
		dispatch(Cases.setRowsPerPage('50'));
		const visibleCases = cases.cases.filter((item: Cases.caseListState) => item.visible);
		setCasesData(visibleCases);
	}, [cases.cases]);

	return (
		<div style={{ position: 'absolute', top: 96, left: 0, right: 0, bottom: 80 }}>
			<div className='OrangeBand'>
				<div style={{ marginLeft: 'calc(10% + 144px)', marginRight: '10%', width: 'calc(80% - 144px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<img style={{ width: 96, marginRight: 8 }} src={hub_logo} alt='Hub-Logo' />
						<div style={{ marginLeft: 24 }}>
							<div style={{ fontFamily: 'OpenSans-Bold', fontSize: 36, marginBottom: 12, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>Main Hub</div>
						</div>
					</div>
					<SearchCases />
				</div>
			</div>
			<div style={{ display: 'flex', alignItems: 'center', width: '100%', backgroundColor: 'white', color: 'black', fontSize: 12 }}>
				<div style={{ padding: '0 8px' }}>Filter by Team:</div>
				<div className='flexGrow2'>
					<select className='WhiteOnOrange' value={cases.filterTeam} onChange={(e) => doTeamOnChange(e)}>
						<option value=''>All Teams</option>
						{cases.teams.map((item: Cases.teamState) => {
							return (
								<option key={item.handler} value={item.handler}>{item.teamname} ({item.name})</option>
							)
						})}
					</select>
				</div>
				<div style={{ padding: '0 8px' }}>Filter by Type:</div>
				<div className='flexGrow2'>
					<select className='WhiteOnOrange' value={cases.filterType} onChange={(e) => doTypeOnChange(e)}>
						<option value=''>All Cases</option>
						<option value='Purchase'>Purchase</option>
						<option value='Remortgage'>Remortgage</option>
						<option value='Sale'>Sale</option>
						<option value='SecuredLending'>Secured Lending</option>
					</select>
				</div>
				<div style={{ padding: '0 8px' }}>Filter by Site:</div>
				<div className='flexGrow2'>
					<select className='WhiteOnOrange' value={cases.filterSite} onChange={(e) => doSiteOnChange(e)}>
						<option value=''>All Sites</option>
						{getCaseSites().map((item: Cases.siteListState) => {
							return (
								<option key={item.siteid} value={(item.siteid ? item.siteid : '<null>')}>{item.site}</option>
							)
						})}
					</select>
				</div>
				<div style={{ padding: '0 8px' }}>Filter by Status:</div>
				<div className='flexGrow2' style={{ position: 'relative' }}>
					<div className='filterByStatus' onClick={() => setFilterByStatusOpened(!filterByStatusOpened)}>
						<p>
							{selectedOptions.length > 0 ? selectedOptions.map((value) => {
								const option = filterByStatusOptions.find(opt => opt.value === value);
								return option ? option.label : '';
							}).join(', ') : 'Select Status'}
						</p>
					</div>

					{filterByStatusOpened && <div className='filterByStatusOptions' ref={filterByStatusDropdownRef}>
						{filterByStatusOptions.map((item, index) => (
							<div className='filterByStatusOptionsBox' key={index}>
								<input type="checkbox" id={item.value} value={item.value} checked={selectedOptions.includes(item.value)} onChange={() => handleFilterByStatusOption(item.value)} />
								<label htmlFor={item.value}>{item.label}</label>
							</div>
						))}
					</div>}
				</div>
				<div style={{ padding: '0 8px' }}>Filter by Wishlist:</div>
				<div className='flexGrow2'>
					<select className='WhiteOnOrange' value={cases.filterWishlist} onChange={(e) => doWishlistOnChange(e)}>
						<option value=''>All Cases</option>
						<option value='W'>Wishlisted Cases</option>
						<option value='NW'>Non Wishlisted Cases</option>
					</select>
				</div>
				<div style={{ padding: '0 8px' }}>Clear Filter:</div>
				<img style={{ width: 20, marginRight: 8 }} src={clear_filter} alt='Clear-Filters-Icon' onClick={() => clearFilters()} />
			</div>
			<div style={{ backgroundColor: 'white' }}>
				<div className="row100">
					<div className="row100">
						<div className="flexGrow1 TabHeaderLeft" style={{ display: 'flex', justifyContent: 'space-between', overflow: 'hidden', textOverflow: 'ellipsis' }} onClick={() => actionOnClick({ type: 'Reference' })}>
							<div style={{ display: 'flex', justifyContent: 'space-between', marginLeft: 8, marginRight: 8, width: '100%', alignItems: 'center' }}>
								<div>Reference</div>
								{cases.sortType.type === 'Reference' && !cases.sortType.reverse &&
									<FontAwesomeIcon style={{ fontSize: 18 }} icon={faCaretUp}></FontAwesomeIcon>
								}
								{cases.sortType.type === 'Reference' && cases.sortType.reverse &&
									<FontAwesomeIcon style={{ fontSize: 18 }} icon={faCaretDown}></FontAwesomeIcon>
								}
							</div>
						</div>
						<div className="flexGrow1 TabHeaderCenter" style={{ display: 'flex', justifyContent: 'space-between', overflow: 'hidden', textOverflow: 'ellipsis' }} onClick={() => actionOnClick({ type: 'Type' })}>
							<div style={{ display: 'flex', justifyContent: 'space-between', marginLeft: 8, marginRight: 8, width: '100%', alignItems: 'center' }}>
								<div>Type</div>
								{cases.sortType.type === 'Type' && !cases.sortType.reverse &&
									<FontAwesomeIcon style={{ fontSize: 18 }} icon={faCaretUp}></FontAwesomeIcon>
								}
								{cases.sortType.type === 'Type' && cases.sortType.reverse &&
									<FontAwesomeIcon style={{ fontSize: 18 }} icon={faCaretDown}></FontAwesomeIcon>
								}
							</div>
						</div>
						<div className="flexGrow1 TabHeaderCenter" style={{ display: 'flex', justifyContent: 'space-between', overflow: 'hidden', textOverflow: 'ellipsis' }} onClick={() => actionOnClick({ type: 'Opened' })}>
							<div style={{ display: 'flex', justifyContent: 'space-between', marginLeft: 8, marginRight: 8, width: '100%', alignItems: 'center' }}>
								<div>Opened</div>
								{cases.sortType.type === 'Opened' && !cases.sortType.reverse &&
									<FontAwesomeIcon style={{ fontSize: 18 }} icon={faCaretUp}></FontAwesomeIcon>
								}
								{cases.sortType.type === 'Opened' && cases.sortType.reverse &&
									<FontAwesomeIcon style={{ fontSize: 18 }} icon={faCaretDown}></FontAwesomeIcon>
								}
							</div>
						</div>
						<div className="flexGrow2 TabHeaderCenter" style={{ display: 'flex', justifyContent: 'space-between', overflow: 'hidden', textOverflow: 'ellipsis' }} onClick={() => actionOnClick({ type: 'Name' })}>
							<div style={{ display: 'flex', justifyContent: 'space-between', marginLeft: 8, marginRight: 8, width: '100%', alignItems: 'center' }}>
								<div>Name</div>
								{cases.sortType.type === 'Name' && !cases.sortType.reverse &&
									<FontAwesomeIcon style={{ fontSize: 18 }} icon={faCaretUp}></FontAwesomeIcon>
								}
								{cases.sortType.type === 'Name' && cases.sortType.reverse &&
									<FontAwesomeIcon style={{ fontSize: 18 }} icon={faCaretDown}></FontAwesomeIcon>
								}
							</div>
						</div>
						<div className="flexGrow2 TabHeaderCenter" style={{ display: 'flex', justifyContent: 'space-between', overflow: 'hidden', textOverflow: 'ellipsis' }} onClick={() => actionOnClick({ type: 'Site' })}>
							<div style={{ display: 'flex', justifyContent: 'space-between', marginLeft: 8, marginRight: 8, width: '100%', alignItems: 'center' }}>
								<div>Site</div>
								{cases.sortType.type === 'Site' && !cases.sortType.reverse &&
									<FontAwesomeIcon style={{ fontSize: 18 }} icon={faCaretUp}></FontAwesomeIcon>
								}
								{cases.sortType.type === 'Site' && cases.sortType.reverse &&
									<FontAwesomeIcon style={{ fontSize: 18 }} icon={faCaretDown}></FontAwesomeIcon>
								}
							</div>
						</div>
						<div className="flexGrow3 TabHeaderCenter" style={{ display: 'flex', justifyContent: 'space-between', overflow: 'hidden', textOverflow: 'ellipsis' }} onClick={() => actionOnClick({ type: 'Address' })}>
							<div style={{ display: 'flex', justifyContent: 'space-between', marginLeft: 8, marginRight: 8, width: '100%', alignItems: 'center' }}>
								<div>Address</div>
								{cases.sortType.type === 'Address' && !cases.sortType.reverse &&
									<FontAwesomeIcon style={{ fontSize: 18 }} icon={faCaretUp}></FontAwesomeIcon>
								}
								{cases.sortType.type === 'Address' && cases.sortType.reverse &&
									<FontAwesomeIcon style={{ fontSize: 18 }} icon={faCaretDown}></FontAwesomeIcon>
								}
							</div>
						</div>
						<div className="flexGrow1 TabHeaderCenter" style={{ display: 'flex', justifyContent: 'space-between', overflow: 'hidden', textOverflow: 'ellipsis' }} onClick={() => actionOnClick({ type: 'Wishlist' })}>
							<div style={{ display: 'flex', justifyContent: 'space-between', marginLeft: 8, marginRight: 8, width: '100%', alignItems: 'center' }}>
								<div>Wishlist</div>
								{cases.sortType.type === 'Wishlist' && !cases.sortType.reverse &&
									<FontAwesomeIcon style={{ fontSize: 18 }} icon={faCaretUp}></FontAwesomeIcon>
								}
								{cases.sortType.type === 'Wishlist' && cases.sortType.reverse &&
									<FontAwesomeIcon style={{ fontSize: 18 }} icon={faCaretDown}></FontAwesomeIcon>
								}
							</div>
						</div>
						<div className="flexGrow1 TabHeaderRight" style={{ display: 'flex', justifyContent: 'space-between', overflow: 'hidden', textOverflow: 'ellipsis' }} onClick={() => actionOnClick({ type: 'Progress' })}>
							<div style={{ display: 'flex', justifyContent: 'space-between', marginLeft: 8, marginRight: 8, width: '100%', alignItems: 'center' }}>
								<div>Progress</div>
								{cases.sortType.type === 'Progress' && !cases.sortType.reverse &&
									<FontAwesomeIcon style={{ fontSize: 18 }} icon={faCaretUp}></FontAwesomeIcon>
								}
								{cases.sortType.type === 'Progress' && cases.sortType.reverse &&
									<FontAwesomeIcon style={{ fontSize: 18 }} icon={faCaretDown}></FontAwesomeIcon>
								}
							</div>
						</div>
					</div>
				</div>
			</div>
			<div style={{ position: 'absolute', top: 227, left: 0, right: 0, bottom: 1, overflowY: 'scroll', height: 'calc(100vh - 454px)' }} onScroll={handleScroll}>
				{casesData?.map((item: Cases.caseListState, index: number) => (
					(index >= (cases.skip * cases.rowsPerPage) && index < ((cases.skip + 1) * cases.rowsPerPage)) && <CaseListItem key={item.caseno} {...item} />
				))}
			</div>
			<div style={{ position: "absolute", bottom: 0, right: 30, width: "100%", backgroundColor: "white", display: "flex", justifyContent: "flex-end", height: '51px' }}>
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: '20px', padding: "0 16px", color: "#000", width: "320px" }}>
					<div style={{ display: "flex", whiteSpace: "nowrap", alignItems: "Center", gap: "10px", position: 'relative', cursor: cases.isLoaded ? 'pointer' : 'not-allowed' }}>Rows Per Page:
						<select value={cases.rowsPerPage} onChange={handleRowsPerPage} className='WhiteOnOrange' style={{ height: '30px', width: '100px', textAlign: 'center', cursor: cases.isLoaded ? 'pointer' : 'not-allowed' }} disabled={!cases.isLoaded}>
							<option value={20}>20</option>
							<option value={50}>50</option>
							<option value={100}>100</option>
							<option value={200}>200</option>
						</select>
						<div className="rowsPerPageArrow arrow right"></div>
					</div>

					<div className='paginationRightContainer'>
						<div style={{ whiteSpace: 'nowrap' }}>{cases.skip + 1} of {Math.ceil(casesData.length / cases.rowsPerPage)}</div>
						<div style={{ display: "flex", gap: "16px" }}>
							<span style={{ opacity: cases.skip - 1 < 0 ? "0.3" : 1, cursor: cases.isLoaded ? 'pointer' : 'not-allowed' }} className="arrow left" onClick={() => handlePageChange(false)}></span>
							<span style={{ opacity: cases.skip + 1 >= Math.ceil(casesData.length / cases.rowsPerPage) ? "0.3" : 1, cursor: cases.isLoaded ? 'pointer' : 'not-allowed' }} className='arrow right' onClick={() => handlePageChange(true)}></span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default CaseList;